import '../chunk-ZAK2ZXQL.js';
import '../chunk-Z2NUSCHM.js';
import '../chunk-C2JZ6HDR.js';
import '../chunk-YLFHP5LL.js';
import '../chunk-EOFYHHEG.js';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../chunk-UKAZWVJA.js';
import '../chunk-G3N5GV7A.js';
import '../chunk-GSFYO3YU.js';
import '../chunk-V4MOACUU.js';
import '../chunk-SWFXEBPS.js';
import { mergeStreamedRows } from '../chunk-WGEOMVHI.js';
import '../chunk-URIMRXBP.js';
import '../chunk-SBPKCERI.js';
import '../chunk-IW2JHXYK.js';
import '../chunk-PEIGN6MO.js';
import '../chunk-GLIMILSP.js';
import '../chunk-NCCYLVD3.js';
import { Field, FormControl } from '../chunk-B25HJXFB.js';
import '../chunk-36R7DNJW.js';
import '../chunk-K4S4BS2H.js';
import '../chunk-GPTAIUJZ.js';
import '../chunk-676CI6I4.js';
import { Input } from '../chunk-NHXI3UAE.js';
import { Badge } from '../chunk-2WDEGOCV.js';
import { Button } from '../chunk-PLP233UC.js';
import { cn } from '../chunk-FFX3LZW7.js';
import * as React from 'react';
import { useOptionalStreamingClient, KNOWN_STREAMS, streamChannelIdsForCaller } from '@authe/streaming';
import { Plus } from 'lucide-react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

// src/stream/readRow.ts
function readStreamRow(message, parse, rowKey) {
  if (parse) return parse(message);
  const payload = message.payload;
  if (!payload || typeof payload !== "object") return null;
  const row = payload;
  return rowKey(row) === null ? null : row;
}

// src/stream/useResourceStream.ts
function channelKey(channel) {
  return typeof channel === "string" ? channel : JSON.stringify(channel);
}
function useResourceStream(options) {
  const { channel, rowKey, parse, enabled = true, max = 500 } = options;
  const client = useOptionalStreamingClient();
  const [rows, setRows] = React.useState([]);
  const [last, setLast] = React.useState(null);
  const rowKeyRef = React.useRef(rowKey);
  const parseRef = React.useRef(parse);
  React.useEffect(() => {
    rowKeyRef.current = rowKey;
    parseRef.current = parse;
  });
  const key = Array.isArray(channel) ? channel.map(channelKey).join("|") : channelKey(channel);
  const channels = React.useMemo(
    () => Array.isArray(channel) ? [...channel] : [channel],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key]
  );
  React.useEffect(() => {
    if (!enabled || !client) return;
    const offs = channels.map((ch) => {
      const handler = (message) => {
        setLast(message);
        const row = readStreamRow(message, parseRef.current, rowKeyRef.current);
        if (row !== null) {
          setRows((prev) => mergeStreamedRows([row], prev, rowKeyRef.current, max));
        }
      };
      return typeof ch === "string" ? client.subscribe(ch, handler) : client.subscribeChannel(ch, handler);
    });
    return () => {
      for (const off of offs) off();
    };
  }, [client, channels, enabled, max]);
  const clear = React.useCallback(() => setRows([]), []);
  return { rows, last, clear };
}
function StreamChannelPicker({
  scope,
  onAdd,
  editableScope = false,
  defaultStream,
  scopeDisabled,
  compact = false,
  className
}) {
  const [stream, setStream] = React.useState(defaultStream ?? KNOWN_STREAMS[0]);
  const [user, setUser] = React.useState(scope.userId ?? "");
  const [teams, setTeams] = React.useState("");
  const [manual, setManual] = React.useState("");
  const disabled = scopeDisabled ?? !(scope.orgId && scope.tenantId);
  const addFromScope = () => {
    const teamIds = teams.split(",").map((team) => team.trim()).filter(Boolean);
    onAdd(
      streamChannelIdsForCaller(
        {
          orgId: scope.orgId,
          tenantId: scope.tenantId,
          userId: (editableScope ? user : scope.userId) || void 0
        },
        stream,
        teamIds
      )
    );
  };
  const addManual = () => {
    const id = manual.trim();
    if (!id) return;
    onAdd([id]);
    setManual("");
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2 rounded-md border bg-muted/20 p-2.5", className), children: [
    /* @__PURE__ */ jsxs("div", { className: cn(compact ? "space-y-2" : "grid grid-cols-1 gap-2 sm:grid-cols-4"), children: [
      /* @__PURE__ */ jsx(Field, { label: "Stream", labelClassName: "text-2xs", ownsControl: true, children: /* @__PURE__ */ jsxs(Select, { value: stream, onValueChange: setStream, children: [
        /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { className: "h-8", children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
        /* @__PURE__ */ jsx(SelectContent, { children: KNOWN_STREAMS.map((name) => /* @__PURE__ */ jsx(SelectItem, { value: name, children: name }, name)) })
      ] }) }),
      editableScope ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Field, { label: "User id (opt)", labelClassName: "text-2xs", children: /* @__PURE__ */ jsx(
          Input,
          {
            value: user,
            onChange: (event) => setUser(event.target.value),
            className: "h-8 font-mono text-xs",
            placeholder: "user_\u2026"
          }
        ) }),
        /* @__PURE__ */ jsx(Field, { label: "Team ids (csv, opt)", labelClassName: "text-2xs", children: /* @__PURE__ */ jsx(
          Input,
          {
            value: teams,
            onChange: (event) => setTeams(event.target.value),
            className: "h-8 font-mono text-xs",
            placeholder: "team_a,team_b"
          }
        ) })
      ] }) : null,
      /* @__PURE__ */ jsx("div", { className: cn(compact ? "" : "flex items-end"), children: /* @__PURE__ */ jsxs(
        Button,
        {
          size: "sm",
          className: cn("h-8", compact ? "w-full" : "w-full"),
          onClick: addFromScope,
          disabled,
          children: [
            /* @__PURE__ */ jsx(Plus, { className: "mr-1.5 size-3.5" }),
            "Add from scope"
          ]
        }
      ) })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-2", children: [
      /* @__PURE__ */ jsx(Field, { label: "\u2026or a raw channel id", className: "min-w-0 flex-1", labelClassName: "text-2xs", children: /* @__PURE__ */ jsx(
        Input,
        {
          value: manual,
          onChange: (event) => setManual(event.target.value),
          onKeyDown: (event) => {
            if (event.key === "Enter") addManual();
          },
          className: "h-8 font-mono text-xs",
          placeholder: "org-tenant-streamed-transactions"
        }
      ) }),
      /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "outline", className: "h-8", onClick: addManual, disabled: !manual.trim(), children: [
        /* @__PURE__ */ jsx(Plus, { className: "mr-1.5 size-3.5" }),
        "Add"
      ] })
    ] })
  ] });
}

// src/stream/summarise.ts
function safeStreamJson(value, space = 2) {
  try {
    return JSON.stringify(value, null, space) ?? String(value);
  } catch {
    return String(value);
  }
}
function summariseStreamPayload(payload, max = 80) {
  if (payload == null || typeof payload !== "object") return String(payload ?? "");
  const record = payload;
  const bits = [];
  if (record.batch === true && Array.isArray(record.items)) {
    return `batch \xB7 ${record.items.length} item${record.items.length === 1 ? "" : "s"}`;
  }
  if (record.resource_type) bits.push(String(record.resource_type));
  if (record.event) bits.push(String(record.event));
  if (record.id) bits.push(String(record.id));
  if (record.canonical_tx_id) bits.push(String(record.canonical_tx_id));
  if (bits.length === 0 && record.chain_id && record.head_block != null) {
    bits.push(String(record.chain_id), `head ${String(record.head_block)}`);
  }
  return bits.join(" \xB7 ") || safeStreamJson(payload, 0).slice(0, max);
}
function StreamEventList({
  entries,
  emptyLabel = "No events yet.",
  maxHeightClassName = "max-h-[40vh]",
  className
}) {
  const [expanded, setExpanded] = React.useState(null);
  return /* @__PURE__ */ jsx("div", { className: cn("overflow-y-auto rounded-md border", maxHeightClassName, className), children: entries.length === 0 ? /* @__PURE__ */ jsx("div", { className: "p-4 text-center text-xs text-muted-foreground", children: emptyLabel }) : /* @__PURE__ */ jsx("ul", { className: "divide-y text-xs", children: entries.map((entry) => /* @__PURE__ */ jsxs("li", { className: "space-y-1 p-2", children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        className: "flex w-full flex-wrap items-center gap-1.5 text-left",
        "aria-expanded": expanded === entry.seq,
        onClick: () => setExpanded((current) => current === entry.seq ? null : entry.seq),
        children: [
          /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: new Date(entry.receivedAt).toLocaleTimeString() }),
          /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "max-w-full truncate font-mono text-3xs", children: entry.channel }),
          /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "text-3xs", children: entry.origin }),
          /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1 truncate text-muted-foreground", children: summariseStreamPayload(entry.payload) })
        ]
      }
    ),
    expanded === entry.seq ? /* @__PURE__ */ jsx("pre", { className: "max-h-56 overflow-auto whitespace-pre-wrap break-all rounded-sm bg-muted/40 p-2 font-mono text-3xs leading-relaxed", children: safeStreamJson(entry.payload) }) : null
  ] }, entry.seq)) }) });
}

export { StreamChannelPicker, StreamEventList, safeStreamJson, summariseStreamPayload, useResourceStream };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map